import type { UIMessage } from '@ai-sdk/react';
import { cn } from '@cobliteam/ui/utils';
import type { ChatStatus, UIDataTypes, UIMessagePart, UITools } from 'ai';
import { parseAsBoolean, useQueryState } from 'nuqs';
import { memo, useCallback, useMemo } from 'react';
import AIChatActions from './AIChatActions';
import AIChatMarkdown, { type AIChatMarkdownComponents, type AIChatMarkdownVariant } from './AIChatMarkdown';

/** Strip legacy mention metadata from old user messages */
const stripSelectedEntitiesPromptMetadata = (value: string) => value.replace(/\n*\[\[AI_SELECTED_ENTITIES::[\s\S]*?::AI_SELECTED_ENTITIES\]\]\s*$/, '').trimEnd();

import { Conversation, ConversationContent, ConversationScrollButton } from './elements/Conversation';
import Loading from './elements/Loading';
import { Message, MessageContent } from './elements/Message';
import { Reasoning, ReasoningContent, ReasoningTrigger } from './elements/Reasoning';
import Submitting from './elements/Submitting';
import ChartsDataTool, { ChartsDataToolSkeleton } from './tools/ChartsDataTool';
import FileTool from './tools/FileTools';
import GenericTool from './tools/GenericTool';
import PerformanceDataTool, { PerformanceDataToolSkeleton } from './tools/PerformanceDataTool';
import VehiclesListTool, { VehiclesSkeleton } from './tools/VehiclesListTool';

interface AIChatMessagesProps {
  className?: string;
  classNameContent?: string;
  markdownComponents?: AIChatMarkdownComponents;
  markdownVariant?: AIChatMarkdownVariant;
  messageContentClassName?: string;
  hideAssistantMessage?: boolean;
  hideUserMessage?: boolean;
  hideTools?: boolean;
  hideAvatar?: boolean;
  hideReasoning?: boolean;
  messages: UIMessage[];
  status: ChatStatus;
  onRegenerate: () => void;
  /** Analytics source identifier */
  source?: string;
}

const AIChatMessages = ({
  messages,
  status,
  className = '',
  classNameContent = '',
  markdownComponents,
  markdownVariant = 'chat',
  messageContentClassName = '',
  hideAvatar = false,
  hideAssistantMessage: _hideAssistantMessage = false,
  hideUserMessage: _hideUserMessage = false,
  hideTools: _hideTools = false,
  hideReasoning: _hideReasoning = false,
  onRegenerate,
  source = 'sidepanel',
}: AIChatMessagesProps) => {
  const [isDebug] = useQueryState('debug', parseAsBoolean.withDefault(false));

  // Get last message info for stable memoization (safe when messages is empty/undefined)
  const lastMessage = messages?.[messages.length - 1] || { role: 'assistant', parts: [] };
  const lastMessageRole = lastMessage?.role;

  const showSubmittedLoader = useMemo(() => {
    return status === 'submitted' && lastMessageRole === 'user';
  }, [status, lastMessageRole]);

  if (isDebug) {
    console.log('messages', messages); // Don't remove this console.
  }

  // Memoize part renderer to prevent recreation
  const renderPart = useCallback(
    (part: UIMessagePart<UIDataTypes, UITools>, messageId: string, messageRole: UIMessage['role'], index: number, isStreaming: boolean, isReasoningStreaming: boolean) => {
      switch (part.type) {
        case 'reasoning':
          return (
            <Reasoning key={`${messageId}-${index}-reasoning`} isStreaming={isReasoningStreaming} className='w-full'>
              <ReasoningTrigger />
              <ReasoningContent>{part.text}</ReasoningContent>
            </Reasoning>
          );
        case 'text':
          return (
            <AIChatMarkdown key={`${messageId}-${index}-text`} isAnimating={isStreaming} variant={markdownVariant} components={markdownComponents}>
              {messageRole === 'user' ? stripSelectedEntitiesPromptMetadata(part.text) : part.text}
            </AIChatMarkdown>
          );
        default:
          return <GenericTool key={`${messageId}-${index}-${part.type}`} part={part} isStreaming={isStreaming} />;
      }
    },
    [markdownVariant, markdownComponents]
  );

  const renderedMessages = useMemo(() => {
    if (!messages || messages.length === 0) return null;
    return messages.map((message, index) => {
      const isLastMessage = index === messages.length - 1;
      const isStreaming = status === 'streaming';
      const isAssistant = message.role === 'assistant';
      const isLastMessageId = message.id === lastMessage?.id;

      return (
        <div className='relative flex-row' key={`${message.id}-${message.role}`}>
          {status !== 'ready' && <Loading key={`${message.id}-loading`} message={message} isAssistant={isAssistant} />}
          <Message from={message.role} hideAvatar={hideAvatar}>
            <MessageContent className={messageContentClassName}>
              {/* TEXT MESSAGE STREAMING */}
              {message.parts.map((part: UIMessagePart<UIDataTypes, UITools>, i: number) => {
                const isReasoningStreaming = isStreaming && isLastMessage && isLastMessageId && i === message.parts.length - 1;
                return renderPart(part, message.id, message.role, i, isStreaming, isReasoningStreaming);
              })}

              {/* LOADING TOOLS ON STREAMING */}
              {isAssistant && isStreaming && isLastMessage && (
                <div>
                  {message.parts.map((part: UIMessagePart<UIDataTypes, UITools>, i: number) => {
                    switch (part.type) {
                      case 'tool-vehiclesListTool':
                        return <VehiclesSkeleton key={`${message.id}-${i}-${part.type}`} />;
                      case 'tool-performanceDataTool':
                        return <PerformanceDataToolSkeleton key={`${message.id}-${i}-${part.type}`} />;
                      case 'tool-chartsDataTool':
                        return <ChartsDataToolSkeleton key={`${message.id}-${i}-${part.type}`} />;
                      default:
                        return null;
                    }
                  })}
                </div>
              )}

              {/* OUTPUT TOOLS ON FINAL */}
              {isAssistant && !isStreaming && (
                <div>
                  {message.parts.map((part: UIMessagePart<UIDataTypes, UITools>, i: number) => {
                    switch (part.type) {
                      case 'tool-vehiclesListTool':
                        return <VehiclesListTool key={`${message.id}-${i}-${part.type}`} part={part} />;
                      case 'tool-code_execution':
                        return <FileTool key={`${message.id}-${i}-${part.type}`} part={part} />;
                      case 'tool-performanceDataTool':
                        return <PerformanceDataTool key={`${message.id}-${i}-${part.type}`} part={part} />;
                      case 'tool-chartsDataTool':
                        return <ChartsDataTool key={`${message.id}-${i}-${part.type}`} part={part} />;
                      default:
                        return null;
                    }
                  })}
                </div>
              )}

              {message.role === 'assistant' && status !== 'streaming' && isLastMessage && (
                <AIChatActions onRegenerate={onRegenerate} message={message as UIMessage<UIDataTypes, UITools>} source={source} />
              )}
            </MessageContent>
          </Message>
        </div>
      );
    });
  }, [messages, status, renderPart, lastMessage?.id, onRegenerate, messageContentClassName, hideAvatar, source]);

  return (
    <Conversation data-testid='ai-chat-messages' className={cn('scroll-container flex-1 scroll-smooth', className)}>
      <ConversationContent className={cn('mx-auto max-w-screen-tablet', classNameContent)}>
        {renderedMessages}
        {showSubmittedLoader && <Submitting />}
      </ConversationContent>
      <ConversationScrollButton />
    </Conversation>
  );
};

export default memo(AIChatMessages);
